import React, { useState, useEffect } from "react";
import { Modal, Checkbox, FormControlLabel } from "@mui/material";
import { RxCross1 } from "react-icons/rx";
import { toast } from "react-toastify";
import { HttpService } from "../../service/HttpService";
import dayjs from "dayjs";

const SelectMatricModal = ({
  open,
  handleClose,
  cultivarId,
  roomId,
  harvestDate,
  initialSelected,
  excludeBuckingId,
  onApply,
}) => {
  const [matricList, setMatricList] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const httpService = new HttpService();

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      return;
    }

    const fetchMatricIds = async () => {
      const formattedDate = harvestDate
        ? dayjs(harvestDate).format("YYYY-MM-DD")
        : "";

      if (!cultivarId || !roomId || !formattedDate) {
        setMatricList([]);
        return;
      }

      setLoading(true);
      try {
        let url = `harvest/plant-count-with-matric?plantId=${cultivarId}&roomId=${roomId}&harvestedDate=${formattedDate}`;
        if (excludeBuckingId) {
          url += `&excludeBuckingId=${excludeBuckingId}`;
        }
        const res = await httpService.get(url);
        const list = res?.data?.matric_ids || [];
        setMatricList(list);

        // Initialize selection
        if (
          initialSelected &&
          Array.isArray(initialSelected) &&
          initialSelected.length > 0
        ) {
          setSelectedIds(new Set(initialSelected));
        } else {
          // Default to empty on first open
          setSelectedIds(new Set());
        }
      } catch (e) {
        console.error(e);
        setMatricList([]);
        setSelectedIds(new Set());
      }
      setLoading(false);
    };

    fetchMatricIds();
  }, [open, cultivarId, roomId, harvestDate]);

  const filteredList = matricList.filter((item) =>
    (item.matric_id || "").toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const isAllFilteredSelected =
    filteredList.length > 0 &&
    filteredList.every((item) => selectedIds.has(item.matric_id || item.id));

  const handleToggleAll = () => {
    const nextSelected = new Set(selectedIds);
    if (isAllFilteredSelected) {
      filteredList.forEach((item) =>
        nextSelected.delete(item.matric_id || item.id),
      );
    } else {
      filteredList.forEach((item) =>
        nextSelected.add(item.matric_id || item.id),
      );
    }
    setSelectedIds(nextSelected);
  };

  const handleToggleItem = (key) => {
    const nextSelected = new Set(selectedIds);
    if (nextSelected.has(key)) {
      nextSelected.delete(key);
    } else {
      nextSelected.add(key);
    }
    setSelectedIds(nextSelected);
  };

  const handleApply = () => {
    const selectedArray = Array.from(selectedIds);
    onApply(selectedArray, selectedArray.length);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="select-matric-modal-title"
    >
      <div
        className="modal_block"
        style={{ width: "90%", maxWidth: 550, boxSizing: "border-box" }}
      >
        <div className="modal_header">
          <div className="modal_title_wrapper">
            <h2 className="modal_title" id="select-matric-modal-title">
              Select Plants (Matric IDs)
            </h2>
          </div>
          <div className="cancel-btn-wrap">
            <RxCross1 onClick={handleClose} style={{ cursor: "pointer" }} />
          </div>
        </div>

        <div
          className="modal_content"
          style={{
            maxHeight: "450px",
            overflowY: "auto",
            padding: "16px 24px",
          }}
        >
          {/* Search Input */}
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <div style={{ flex: 1, position: "relative" }}>
              <input
                type="text"
                placeholder="Search Matric IDs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 36px 10px 14px",
                  fontSize: "14px",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {searchQuery && (
                <RxCross1
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#6c757d",
                    fontSize: "16px",
                  }}
                />
              )}
            </div>
          </div>

          {!loading && matricList.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                background: "#e9ecef",
                borderRadius: "6px",
                marginBottom: "12px",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllFilteredSelected}
                    onChange={handleToggleAll}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <span style={{ fontSize: "14px", fontWeight: 600 }}>
                    Select All ({filteredList.length})
                  </span>
                }
              />
              <span
                style={{
                  fontSize: "13px",
                  color: "#495057",
                  fontWeight: 500,
                }}
              >
                Selected: {selectedIds.size} / {matricList.length}
              </span>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              Loading Matric IDs...
            </div>
          ) : matricList.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#666" }}
            >
              No Matric IDs found for selected Harvest Date, Cultivar, and Room.
            </div>
          ) : filteredList.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#666" }}
            >
              No matching Matric IDs found.
            </div>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {filteredList.map((item, idx) => {
                const key = item.matric_id || item.id;
                const checked = selectedIds.has(key);
                return (
                  <div
                    key={item.id || idx}
                    onClick={() => handleToggleItem(key)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: checked ? "#e6f6f1" : "#f8f9fa",
                      borderRadius: "6px",
                      border: checked
                        ? "1px solid #00A272"
                        : "1px solid #e9ecef",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        onChange={() => handleToggleItem(key)}
                        color="primary"
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span style={{ fontWeight: 500, color: "#495057" }}>
                        {item.matric_id || "N/A"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          className="modal_footer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{ fontSize: "13px", fontWeight: 600, color: "#00A272" }}
          >
            {selectedIds.size} Plant(s) Selected
          </span>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              className="btn btn-border"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleApply}
              disabled={loading || matricList.length === 0}
            >
              Apply Selection
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SelectMatricModal;
